import type {
  IIsSelectedQualityAllowedParams,
  IUsePlayerSubscriptionLimitations,
  IUsePlayerSubscriptionLimitationsParams,
} from './lib'

import { Events } from '@setplex/pureya'
import { useUnit } from 'effector-react'
import { useCallback, useEffect } from 'react'
import {
  entitySubscriptionsModel,
  getMaxAllowedPixelsBySubscription,
} from '~/entities/subscriptions'
import {
  CastOptionIsNotAllowedWidget,
  castOptionIsNotAllowedWidgetModel,
} from './CastOptionIsNotAllowedWidget'
import {
  InfoUpgradeCurrentPackageWidget,
  infoUpgradeCurrentPackageWidgetModel,
  isVideoQualityBlocked,
} from './InfoUpgradeCurrentPackageWidget'
import {
  UpgradeCurrentPackageWidget,
  upgradeCurrentPackageWidgetModel,
} from './UpgradeCurrentPackageWidget'

export const usePlayerSubscriptionLimitations = ({
  sourceUrl,
  miniPlayer,
  player,
  isRewound,
}: IUsePlayerSubscriptionLimitationsParams): IUsePlayerSubscriptionLimitations => {
  const currentSubscription = useUnit(
    entitySubscriptionsModel.$currentSubscription
  )

  // Info upgrade current package widget
  const isVideoBlocked = useUnit(
    infoUpgradeCurrentPackageWidgetModel.$isVideoBlocked
  )
  const showInfoUpgradePackageWidget = useUnit(
    infoUpgradeCurrentPackageWidgetModel.$showInfoUpgradePackageWidget
  )
  const hideInfoUpgradePackageWidget = useUnit(
    infoUpgradeCurrentPackageWidgetModel.hideInfoUpgradePackageWidget
  )
  const isVideoQualityAvailabile = useUnit(
    infoUpgradeCurrentPackageWidgetModel.isVideoQualityAvailabile
  )

  // Upgrade current package widget
  const hideUpgradePackageWidget = useUnit(
    upgradeCurrentPackageWidgetModel.hideUpgradePackageWidget
  )
  const isShowUpgradePackageWidget = useUnit(
    upgradeCurrentPackageWidgetModel.$showUpgradePackageWidget
  )
  const showUpgradePackageWidget = useUnit(
    upgradeCurrentPackageWidgetModel.showUpgradePackageWidget
  )

  // Cast option is not allowed widget
  const isShowCastNotAllowedWidget = useUnit(
    castOptionIsNotAllowedWidgetModel.$showCastNotAllowedWidget
  )
  const showCastNotAllowedWidget = useUnit(
    castOptionIsNotAllowedWidgetModel.showCastNotAllowedWidget
  )
  const hideCastNotAllowedWidget = useUnit(
    castOptionIsNotAllowedWidgetModel.hideCastNotAllowedWidget
  )
  const setIsRewinded = useUnit(
    infoUpgradeCurrentPackageWidgetModel.setIsRewinded
  )

  useEffect(() => {
    setIsRewinded(false)
  }, [setIsRewinded])

  useEffect(() => {
    return () => {
      hideInfoUpgradePackageWidget()
      hideUpgradePackageWidget()
      hideCastNotAllowedWidget()
    }
  }, [
    sourceUrl,
    hideInfoUpgradePackageWidget,
    hideUpgradePackageWidget,
    hideCastNotAllowedWidget,
  ])

  const isSelectedQualityAllowed = useCallback(
    ({ quality, manuallySelected }: IIsSelectedQualityAllowedParams) => {
      const maxQuality = currentSubscription?.maxQuality

      if (!maxQuality) return true

      const maxAllowedPixels = getMaxAllowedPixelsBySubscription(maxQuality)

      const isAllowed = quality <= maxAllowedPixels

      if (!isAllowed && manuallySelected) {
        showUpgradePackageWidget(quality)
      }

      return isAllowed
    },
    [currentSubscription, showUpgradePackageWidget]
  )

  const isCastOptionAllowed = useCallback(() => {
    const isAllowed = !currentSubscription?.disableCastOption

    if (!isAllowed) {
      showCastNotAllowedWidget()
    }

    return isAllowed
  }, [currentSubscription, showCastNotAllowedWidget])

  const handleIsVideoQualityBlocked = useCallback(
    ({ quality }: { quality: number }) => {
      return isVideoQualityBlocked({
        minVideoQuality: quality,
        currentSubscription,
        isVideoQualityAvailabile,
      })
    },
    [currentSubscription, isVideoQualityAvailabile]
  )

  useEffect(() => {
    if (isVideoBlocked) {
      player?.instance?.addClass('vjs-x-player-disabled-playing')
    }
  }, [player, isVideoBlocked])

  const isSubscriptionPlayerDisabled =
    isVideoBlocked ||
    isShowUpgradePackageWidget ||
    isShowCastNotAllowedWidget ||
    showInfoUpgradePackageWidget

  useEffect(() => {
    if (isSubscriptionPlayerDisabled) {
      player?.instance?.addClass('vjs-controls-disabled')
    } else {
      player?.instance?.removeClass('vjs-controls-disabled')
    }
  }, [player, isSubscriptionPlayerDisabled])

  // TODO: Look for ways to improve this logic using functions in events
  // For example sync/async events responsible for checking permissions outside pureya
  useEffect(() => {
    player.instance?.trigger(
      Events.Player.IsSelectedQualityAllowed,
      isSelectedQualityAllowed
    )
  }, [isSelectedQualityAllowed, player.instance])

  useEffect(() => {
    player.instance?.trigger(
      Events.Player.IsCastOptionAllowed,
      isCastOptionAllowed
    )
  }, [isCastOptionAllowed, player.instance])

  useEffect(() => {
    player.instance?.trigger(
      Events.Player.IsVideoQualityBlocked,
      handleIsVideoQualityBlocked
    )
  }, [handleIsVideoQualityBlocked, player.instance])

  const smallPlayer = miniPlayer ? !player.instance?.isFullscreen() : undefined

  const modals = (
    <>
      <UpgradeCurrentPackageWidget smallPlayer={smallPlayer} />
      <CastOptionIsNotAllowedWidget smallPlayer={smallPlayer} />
      <InfoUpgradeCurrentPackageWidget
        smallPlayer={smallPlayer}
        isRewound={isRewound}
      />
    </>
  )

  return {
    subscriptionLimitationsModals: modals,
    isVideoBlocked,
    isSelectedQualityAllowed,
    isCastOptionAllowed,
    isVideoQualityBlocked: handleIsVideoQualityBlocked,
    setIsRewinded,
  }
}
